import {useEffect, useState} from "react";
import {useHistory} from "react-router-dom";
import {Routes} from "../../../../common/Routes";
import {isNullOrUndefined} from "../../../../common/utils";
import {createConditionTree} from "../../../../components/condition/utils/createConditionTree";
import {findListOfKeyConditions} from "../../../../components/condition/utils/findListOfKeyConditions";
import {validateMembershipCondition} from "../../../../components/condition/utils/validateMembershipCondition";
import {errorMsg} from "../../../../components/simpleMessageAlert";
import {sendUserGroupDetailsEvent, UserGroupDetailsAction} from "../../../../components/matomoClient";
import {useDispatch, useSelector} from "react-redux";
import {InfoTabUsersTableContentActions} from "../../../../components/InfoTab/model";
import {patchExcludedMembers} from "../model";
import {findSpecialConditionByUserId} from "../../../../components/condition/utils/findSpecialConditionByUserId";
import {KeyConditionAction} from "../../KeyCondition/model";
import {specialConditionsColumnGenerator} from "./specialConditionsColumnGenerator";
import {excludeMembersColumnGenerator} from "./excludedMembersTableColumnGenerator";
import { useIsIndividualInclusionNamingEnabled } from "../../KeyCondition/common/hooks/IsIndividualInclusionNamingEnabled";

export const MembershipConditionsController = (
  membershipCondition,
  individualInclusions,
  haveMembershipConditionsEditAccess,
  userGroupUniqueName,
  userGroupTypeUniqueName,
) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isIndividualInclusionNamingEnabled = useIsIndividualInclusionNamingEnabled();
  const [memberToBeRemoved, setMemberToBeRemoved] = useState("");
  const [isRemoveExcludedMemberModalVisible, setIsRemoveExcludedMemberModalVisible] = useState(false);

  const membershipConditionsTree = createConditionTree(membershipCondition, null);
  const excludedMembers =
    useSelector((rootState) => rootState.userGroup.userGroupDetails[userGroupUniqueName].value.excludedMembers);

  let isMembershipConditionError = false;
  let membershipConditionErrorCode;
  let membershipConditionErrorMessage;
  let membershipErrorConditionId;
  const membershipConditionError = validateMembershipCondition(membershipConditionsTree);
  if (!isNullOrUndefined(membershipConditionError.errorCode)) {
    isMembershipConditionError = true;
    membershipConditionErrorCode = membershipConditionError.errorCode;
    membershipConditionErrorMessage = membershipConditionError.message;
    membershipErrorConditionId = membershipConditionError.conditionId;
  }

  const listOfKeyConditions = [];
  if (isMembershipConditionError === false) {
    findListOfKeyConditions(membershipConditionsTree, listOfKeyConditions);
  }

  const propsForSpecialConditions = {
    userGroupUniqueName,
    userGroupTypeUniqueName,
    membershipConditionsTree,
  };
  const specialConditionsColumn = specialConditionsColumnGenerator(
    individualInclusions,
    haveMembershipConditionsEditAccess,
    propsForSpecialConditions,
    navigateToEditSpecialConditionPage,
    navigateToDeleteSpecialConditionPage,
    isIndividualInclusionNamingEnabled
  );

  const onRemoveMemberButtonClick = (userId) => {
    setMemberToBeRemoved(userId);
    setIsRemoveExcludedMemberModalVisible(true);
  }

  const onRemoveExcludedMemberConfirmation = () => {
    dispatch(patchExcludedMembers({
      userGroupUniqueName: userGroupUniqueName,
      excludeMemberRequest: {
        excludedMembers: excludedMembers
          .filter((member) => member.userId !== memberToBeRemoved)
          .map(({userId, reason}) => ({userId, reason}))
      }
    }));
    setIsRemoveExcludedMemberModalVisible(false);
  }

  function navigateToKeyConditionsPage() {
    history.push(
      `${Routes.UserGroups.basePath}/${userGroupTypeUniqueName}/${userGroupUniqueName}/key-condition/add`,
    );
    sendUserGroupDetailsEvent(UserGroupDetailsAction.AddMembershipCondition);
  }

  useEffect(() => {
    if (isMembershipConditionError === true) {
      errorMsg(
        `${membershipConditionErrorMessage} - ${membershipConditionErrorCode} in condition ${membershipErrorConditionId}`,
      );
    }
  }, [isMembershipConditionError]);

  const userGroupDetails = useSelector(
    (rootState) => rootState.userGroup.userGroupDetails[userGroupUniqueName]?.value
  );

  const userGroupUsersDetails = useSelector(
    (rootState) =>
      rootState.InfoTabUsersTableContent.userGroupUserInfoTableContent[userGroupUniqueName]?.[
        "members"
        ]?.value
  );

  function navigateToDeleteSpecialConditionPage(userId, propsForSpecialConditions) {
    const specialCondition = findSpecialConditionByUserId(
      propsForSpecialConditions.membershipConditionsTree,
      userId,
    );
    history.push(
      `${Routes.UserGroups.basePath}/${propsForSpecialConditions.userGroupTypeUniqueName}/${propsForSpecialConditions.userGroupUniqueName}/key-condition/${KeyConditionAction.DELETE}/${specialCondition.key}`,
    );
  }

  function navigateToEditSpecialConditionPage(userId, propsForSpecialConditions) {
    const specialCondition = findSpecialConditionByUserId(
      propsForSpecialConditions.membershipConditionsTree,
      userId,
    );
    history.push(
      `${Routes.UserGroups.basePath}/${propsForSpecialConditions.userGroupTypeUniqueName}/${propsForSpecialConditions.userGroupUniqueName}/key-condition/${KeyConditionAction.EDIT}/${specialCondition.key}`,
    );
  }

  useEffect(() => {
    dispatch(InfoTabUsersTableContentActions.getUserGroupMembersTableContent(userGroupUniqueName));
  }, [userGroupUniqueName]);

  function updateExcludeRequest(selectedRows, reason) {
    const selectedMembers = selectedRows?.map(x => (
      {
        userId: x.key,
        reason: reason
      }
    ));
    const excludeRequest = {
      excludedMembers: selectedMembers
    }
    dispatch(patchExcludedMembers({
      userGroupUniqueName,
      excludeMemberRequest: excludeRequest,
    }));
  }

  const excludedMembersDetails = (userGroupUsersDetails?.dataSource && excludedMembers)
    ? excludedMembers
      .map((member) => {
        const user = userGroupUsersDetails.dataSource.find((user) => user.key === member.userId);
        return ({
          userEmployeeId: user.user.second,
          userEmail: user.email,
          userName: user.user.first,
          userId: member.userId,
          reason: member.reason,
          excludedBy: member.excludedBy,
          excludedOn: member.excludedOn,
        })
      })
    : [];

  const excludedMembersColumns = excludeMembersColumnGenerator(
    excludedMembersDetails,
    haveMembershipConditionsEditAccess,
    onRemoveMemberButtonClick
  );

  function getRemoveExcludedMemberMessage() {
    const user = userGroupUsersDetails?.dataSource?.find((user) => user.key === memberToBeRemoved);
    return user
      ? (
        <>
          Are you sure you want to remove exclusion for the member
          <b> {user.user.first}, ({user.user.second})</b>?
        </>
      )
      : "";
  }

  const shouldRenderRemoveExcludedMemberModal = () => isRemoveExcludedMemberModalVisible

  function onRemoveExcludedMemberCancel() {
    setIsRemoveExcludedMemberModalVisible(false);
  }

  return {
    listOfKeyConditions,
    specialConditionsColumn,
    navigateToKeyConditionsPage,
    isMembershipConditionError,
    userGroupDetails,
    userGroupUsersDetails,
    updateExcludeRequest,
    excludedMembersDetails,
    excludedMembersColumns,
    getRemoveExcludedMemberMessage,
    onRemoveExcludedMemberConfirmation,
    onRemoveExcludedMemberCancel,
    shouldRenderRemoveExcludedMemberModal,
  };
};
